// @ts-nocheck
import React, { Fragment, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Header as SPCHeader } from 'sonata-platform-components';
import {
  ButtonUI,
  CustomThemeProvider,
  Loader,
  Tooltip,
  TooltipInteractionEnum,
  useQueryFetch
} from 'sonata-react-components';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { ReportingConnectSonataLIModal } from '@/modules/reporting/ReportingHeader/ReportingConnectSonataLIModal';
import { ReportingShareAdvanceReportModal } from '@/modules/reporting/ReportingHeader/ReportingShareAdvanceReportModal';
import { ReportingShareSimpleReportModal } from '@/modules/reporting/ReportingHeader/ReportingShareSimpleReportModal';
import { getLastContextualReport } from '@/modules/reporting/reporting.resources';
import { getCoverageStudyReportStatus, reportingProgressGetMetricAndKpi } from '@/store/selectors/reporting.selectors';
import { AIInsightsModal } from '@/ts-modules/reporting/AIInsightsModal';
import { DownloadPDFButton } from '@/ts-modules/reporting/Header/DownloadPDFButton';
import DownloadPDFButtonV2 from '@/ts-modules/reporting/Header/DownloadPDFButton/DownloadPDFButtonV2';
import { Shortcuts } from '@/ts-modules/reporting/Header/Shortcuts';
import { ReportDownloadSection } from '@/ts-modules/reporting/ReportDownload';
import MyReportsModal from '@/ts-modules/reporting/ReportDownload/MyReportsModal/MyReportsModal';
import { getLastGeoLookALikeReport } from '@/ts-resources/reporting/geoLookALike.resources';
import { getLastPdfReport } from '@/ts-resources/reporting/pdfReport.resources';
import CoverageStudySection from './CoverageStudy/CoverageStudySection';
import { getLastCoverageReport } from './CoverageStudy/resources';
import { HeaderDivider, HeaderStyle } from './header.style';
import LastDownloads from './LastDownloads/LastDownloads';

import type { GeoDictionary } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';
import type { ButtonUIProps } from 'sonata-react-components/lib/types/src_ts/components/ButtonUI/ButtonUI.types';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { ReportConfigInitialData } from '@/ts-modules/reporting/ReportDownload/types';
import type { CampaignItem } from './Campaigns/Multi/components/EditSelectionModal/EditSelectionModal';

type HeaderButton = ButtonUIProps & {
  campaignProps?: any;
  customAction?: string;
  elementTargetId?: string;
  onClick?: (...args: any[]) => void;
  permission?: string;
  tooltipReference?: string;
};

// todo whenever reporting backbones dies remove these campaigns, cartography, stats and showGTotal props. Use
// todo the context useReporting to get these same params
type Props = {
  campaigns: CampaignItem[];
  cartography: Partial<GeoDictionary>[];
  handleIdfaReportModalToggled?: () => void; // todo to be removed when reporting backbone dies
  handleReportDownloadModalToggled?: () => void; // todo to be removed when reporting backbone dies
  id: string;
  noStats?: boolean;
  showBackButton?: boolean;
  showGTotal?: boolean;
  stats: Record<string, any>;
  titleNav?: string;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

type State = {
  aiInsightsDataReady: boolean;
  aiInsightsLoading: boolean;
  aiInsightsModalIsOpen: boolean;
  coverageStudyIsOpen: boolean;
  downloadReportIsOpen: boolean;
  initialData?: ReportConfigInitialData;
  isPDFLoading: boolean;
  myReportModalIsOpen: boolean;
  sharedReport: {
    sharedReportId: string | undefined;
    sharedReportPassword: string | undefined;
  };
};

function Header({
  campaigns,
  cartography,
  handleIdfaReportModalToggled,
  handleReportDownloadModalToggled,
  id,
  noStats = false,
  showBackButton = true,
  showGTotal,
  stats,
  titleNav = 'reporting.header.title',
  user,
  userRoles
}: Props) {
  const { formatMessage } = useIntl();
  const params = new URLSearchParams(window.location.search);
  const isReportingV2 = window.location.hash.includes('reporting-v2');
  const isSharedReport = Boolean(params.get('shared-report') || params.get('report'));
  const progressEvents = useSelector(store => reportingProgressGetMetricAndKpi(store));
  const coverageStudyReportStore = useSelector(getCoverageStudyReportStatus);

  const campaignIds: string[] = campaigns.map(c => c.id);
  const campaignNames: string[] = campaigns.map(c => c.name);
  const campaignTypes: string[] = campaigns.map(c => c.type);

  const [
    {
      aiInsightsDataReady,
      aiInsightsLoading,
      aiInsightsModalIsOpen,
      coverageStudyIsOpen,
      downloadReportIsOpen,
      initialData,
      isPDFLoading,
      myReportModalIsOpen,
      sharedReport: { sharedReportId, sharedReportPassword }
    },
    setState
  ] = useState<State>({
    aiInsightsDataReady: false,
    aiInsightsLoading: false,
    aiInsightsModalIsOpen: false,
    coverageStudyIsOpen: false,
    downloadReportIsOpen: false,
    isPDFLoading: false,
    myReportModalIsOpen: false,
    sharedReport: {
      sharedReportId: undefined,
      sharedReportPassword: undefined
    }
  });

  const hasReportingDownloadV2 = checkUserResourcePermissions('reportingDownloadV2', user, userRoles);
  const hasAIInsightsPermission = checkUserResourcePermissions('reportingAIInsights', user, userRoles);

  const hideReporting = (hide: boolean) => {
    const classAction = hide ? 'add' : 'remove';
    const reportingDiv = document.getElementsByClassName('reporting');
    reportingDiv[0].classList[classAction]('hidden');
    const footerDiv = document.getElementsByClassName('footer');
    footerDiv[0].classList[classAction]('hidden');
  };

  const handleDownloadReportToggled = (newInitialData?: ReportConfigInitialData) => {
    hideReporting(true);
    setState(prevState => ({
      ...prevState,
      downloadReportIsOpen: true,
      initialData: newInitialData
    }));
  };

  const handleDownloadReportBackToggled = () => {
    hideReporting(false);
    setState(prevState => ({
      ...prevState,
      downloadReportIsOpen: false
    }));
  };

  const handleCoverageStudyToggled = (isOpen: boolean) => {
    hideReporting(isOpen);
    setState(prevState => ({
      ...prevState,
      coverageStudyIsOpen: isOpen
    }));
  };

  // biome-ignore assist/source/useSortedKeys: The order of the buttons is important
  const buttons = {
    downloadPdf: {
      className: 'customButton',
      customAction: 'download_pdf',
      elementTargetId: 'sonata-reporting',
      icon: 'fa-regular fa-file-pdf',
      permission: 'reportingDownloadPdf',
      shape: 'link' as const,
      size: 'small' as const,
      text: `Insights Report ${hasReportingDownloadV2 ? 'V1' : ''}`,
      type: 'secondary' as const
    },
    downloadPdfV2: {
      className: 'customButton',
      customAction: 'download_pdf_v2',
      elementTargetId: 'sonata-reporting',
      icon: 'fa-regular fa-file-pdf',
      permission: 'reportingDownloadV2',
      shape: 'link' as const,
      size: 'small' as const,
      text: 'Insights Report',
      type: 'secondary' as const
    },
    downloadSmartPdfV2: {
      className: 'customButton',
      customAction: 'download_smart_pdf_v2',
      elementTargetId: 'sonata-reporting',
      icon: 'fa-regular fa-file-pdf',
      permission: 'reportingDownloadV2',
      shape: 'link' as const,
      size: 'small' as const,
      text: 'Smart Insights Report',
      type: 'secondary' as const
    },
    coverageStudy: {
      className: 'customButton',
      icon: 'fas fa-file-spreadsheet',
      loading: coverageStudyReportStore.status === 'pending',
      onClick: () => handleCoverageStudyToggled(true),
      permission: 'reportingCoverageStudy',
      shape: 'link' as const,
      size: 'small' as const,
      text: 'Coverage Study',
      type: 'secondary' as const,
      visible: campaignTypes.every(t => t === CampaignFamilyEnum.dooh)
    },
    ...(!isReportingV2 && {
      downloadReport: {
        className: 'reportingHeader__downloadButtonsButton',
        icon: 'fas fa-file-arrow-down ',
        onClick: () => handleReportDownloadModalToggled?.(),
        shape: 'link' as const,
        size: 'small' as const,
        text: `Raw Campaign Data ${hasReportingDownloadV2 ? 'V1' : ''}`,
        type: 'secondary' as const,
        visible: !noStats
      }
    }),
    downloadReportV2: {
      className: 'customButton',
      icon: 'fas fa-download',
      onClick: (initialReport?: ReportConfigInitialData) => handleDownloadReportToggled(initialReport),
      permission: 'reportingDownloadV2',
      shape: 'link' as const,
      size: 'small' as const,
      text: 'Raw Campaign Data',
      type: 'secondary' as const,
      visible: !noStats
    }
  };

  const menu = {
    ...(!isReportingV2 && {
      userReport: {
        className: 'customButton',
        icon: 'fa-regular fa-user',
        onClick: () => handleIdfaReportModalToggled?.(),
        permission: 'reportingIdfaReports',
        shape: 'link' as const,
        size: 'small' as const,
        text: 'User ID Report',
        type: 'secondary' as const,
        visible: !noStats
      }
    }),
    connectWithSonataLI: {
      className: 'customButton',
      customAction: 'connect_with_sonataLI',
      elementTargetId: 'sonata-reporting',
      icon: 'fa-regular fa-circle-bolt',
      permission: 'reportingConnectSonataLI',
      shape: 'link' as const,
      size: 'small' as const,
      text: 'Connect with Sonata LI',
      type: 'secondary' as const
    },
    shareReport: {
      className: 'customButton',
      customAction: 'share_report',
      elementTargetId: 'sonata-reporting',
      icon: 'fas fa-link',
      permission: 'reportingShareLink',
      shape: 'link' as const,
      size: 'small' as const,
      text: 'Share Public Link',
      type: 'secondary' as const
    }
  };

  const { data: lastContextualDiscoveryReportStorage } = useQueryFetch(
    ['lastContextualDiscoveryReport', user.id, campaignIds],
    async () => getLastContextualReport(user.id, campaignIds)
  );

  const lastCoverageStudyReportToken = {};
  const { data: lastCoverageStudyReportStorage } = useQueryFetch(
    ['lastCoverageStudyReport', user.id, campaignIds],
    async () => getLastCoverageReport(user.id as string, campaignIds[0], lastCoverageStudyReportToken)
  );

  const lastGeoLookALikeReportToken = {};
  const { data: lastGeoLookALikeReportStorage } = useQueryFetch(
    ['lastGeoLookALikeReport', user.id, campaignIds],
    async () => getLastGeoLookALikeReport(user.id!, campaignIds, 'reporting', lastGeoLookALikeReportToken)
  );

  const lastPdfReportToken = {};
  const { data: lastPdfReport } = useQueryFetch(['lastPdfReport', user.id, campaignIds], async () =>
    getLastPdfReport(user.id!, campaignIds, 'reporting-base-template-with-subtemplateId', lastPdfReportToken)
  );

  const shouldShowBadge =
    lastContextualDiscoveryReportStorage?.status === 'success' ||
    lastCoverageStudyReportStorage?.status === 'success' ||
    lastGeoLookALikeReportStorage?.status === 'success' ||
    lastPdfReport?.status === 'success';

  const handleAIButtonClick = () => {
    setState(prevState => ({
      ...prevState,
      aiInsightsModalIsOpen: true
    }));
  };

  const handleMyReportsButtonClick = () => {
    setState(prevState => ({
      ...prevState,
      myReportModalIsOpen: true
    }));
  };

  const handleMyReportsModalClose = (report?: ReportConfigInitialData) => {
    setState(prevState => ({
      ...prevState,
      myReportModalIsOpen: false
    }));
    if (report) {
      buttons.downloadReportV2.onClick?.(report);
    }
  };

  const handleAIInsightsClose = () => {
    setState(prevState => ({
      ...prevState,
      aiInsightsModalIsOpen: false
    }));
  };

  const sharedReportCallback = useCallback((newSharedReportId: string, newSharedReportPassword: string) => {
    setState(prevState => ({
      ...prevState,
      sharedReport: {
        sharedReportId: newSharedReportId,
        sharedReportPassword: newSharedReportPassword
      }
    }));
  }, []);

  const getCampaignMenuItems = (campaignId: string, campaignType: string) => {
    const wizardUrl = isDOOHCampaign(campaignType) ? taptap.config.wizardUrl : '#!/';
    return [
      {
        link: `${wizardUrl}campaign/${campaignId}/step/1`,
        text: formatMessage({ id: 'campaignStepsMenu.locations' })
      },
      {
        link: `${wizardUrl}campaign/${campaignId}/step/2`,
        text: formatMessage({ id: 'campaignStepsMenu.settingsAudience' })
      },
      {
        link: `${wizardUrl}campaign/${campaignId}/step/3`,
        text: formatMessage({ id: 'campaignStepsMenu.adCreatives' })
      }
    ];
  };

  const getList = () => {
    if (campaignIds.length === 1) {
      return getCampaignMenuItems(campaignIds[0], campaignTypes[0]);
    }
    const formattedListItems: { auxClass?: string; link: string | null; text: string }[] = [];
    campaignIds.forEach((campaignId, indexKey) => {
      formattedListItems.push(
        {
          auxClass: 'campaignName',
          link: null,
          text: campaignNames[indexKey]
        },
        ...getCampaignMenuItems(campaignId, campaignTypes[indexKey])
      );
    });
    return formattedListItems;
  };

  const handleTogglePDFLoading = (val: boolean) => {
    setState(prevState => ({
      ...prevState,
      isPDFLoading: val
    }));
  };

  const getButton = (buttonProps: HeaderButton, i: number) => {
    const { campaignProps, customAction, elementTargetId, onClick, tooltipReference, ...rest } = buttonProps;
    switch (customAction) {
      case 'download_pdf':
        return (
          <DownloadPDFButton
            key={i}
            onTogglePDFLoading={handleTogglePDFLoading}
            sonataButtonProps={buttonProps as React.ComponentProps<typeof DownloadPDFButton>['sonataButtonProps']}
          />
        );
      case 'share_report':
        return checkUserResourcePermissions('reportingAdvancedShareLink', user, userRoles) ? (
          <ReportingShareAdvanceReportModal buttonProps={{ ...rest }} key={i} user={user} userRoles={userRoles} />
        ) : (
          <ReportingShareSimpleReportModal
            buttonProps={{ ...rest }}
            key={i}
            sharedReportCallback={sharedReportCallback}
            sharedReportId={sharedReportId}
            sharedReportPassword={sharedReportPassword}
          />
        );
      case 'connect_with_sonataLI':
        return <ReportingConnectSonataLIModal buttonProps={{ ...rest }} key={i} />;
      case 'download_pdf_v2':
        return (
          <DownloadPDFButtonV2
            buttonProps={{ ...rest }}
            id="download_pdf_v2"
            key={i}
            omitAIInsights
            selectedCampaigns={campaigns}
          />
        );
      case 'download_smart_pdf_v2':
        return (
          <DownloadPDFButtonV2
            buttonProps={{ ...rest }}
            id="download_smart_pdf_v2"
            key={i}
            selectedCampaigns={campaigns}
          />
        );
      default:
        return <ButtonUI key={i} onClick={onClick} {...rest} />;
    }
  };

  const renderItem = (buttonProps: HeaderButton, i: number) => {
    const { permission } = buttonProps;
    if (!checkUserResourcePermissions(permission!, user, userRoles)) {
      return null;
    }
    return getButton(buttonProps, i);
  };

  const renderMenu = () => {
    const dividerIndexes = [2];
    return (
      <div className="reportingHeader__menu">
        {Object.values(menu)
          .map(renderItem)
          .map((b, i) => (
            <Fragment key={i}>
              {dividerIndexes.includes(i) && <HeaderDivider />}
              {b}
            </Fragment>
          ))}
      </div>
    );
  };

  const showEdit = checkUserResourcePermissions('campaignEdition', user, userRoles) && campaignIds.length === 1;

  const list = getList();

  const handleAIInsightsLoading = (newLoadingState: boolean) => {
    setState(prevState => ({
      ...prevState,
      aiInsightsLoading: newLoadingState
    }));
  };

  const handleAIInsightsDataReady = (newDataReadyState: boolean) => {
    setState(prevState => ({
      ...prevState,
      aiInsightsDataReady: newDataReadyState
    }));
  };

  return (
    <>
      <HeaderStyle className="reportingHeader">
        <SPCHeader className="reportingHeader__container">
          <SPCHeader.Title>
            {showBackButton && (
              <a className="reportingHeader__backLink" href={`${taptap?.config?.sonataAppUrl}#!/campaigns`}>
                <i className="fal fa-arrow-left reportingHeader__backLinkIcon" />
              </a>
            )}
            {formatMessage({
              defaultMessage: 'Campaign Report',
              id: titleNav
            })}
            {showEdit && (
              <Tooltip
                config={{
                  arrow: true,
                  component: (
                    <div className="reportingHeader__titleMenu">
                      {list.map(item => (
                        <a href={item.link!} key={item.text}>
                          <ButtonUI
                            className="reportingHeader__titleMenuButton"
                            shape="link"
                            text={item.text}
                            type="secondary"
                          />
                        </a>
                      ))}
                    </div>
                  ),
                  forcePlacement: false,
                  interactions: [TooltipInteractionEnum.click],
                  padding: '0',
                  placement: 'bottom',
                  portal: false,
                  strategy: 'fixed'
                }}
              >
                <ButtonUI
                  className="reportingHeader__menuButton"
                  icon="fas fa-edit"
                  shape="link"
                  size="ultraSmall"
                  text={formatMessage({
                    defaultMessage: 'Edit Campaign',
                    id: 'reporting.header.buttons.editCampaign'
                  })}
                  type="secondary"
                />
              </Tooltip>
            )}
          </SPCHeader.Title>
          <SPCHeader.Actions>
            {hasAIInsightsPermission && (
              <CustomThemeProvider themeContrast="darkAI">
                <ButtonUI
                  badge={aiInsightsDataReady}
                  className="reportingHeader__aiInsightsButton"
                  disabled={campaigns.some(campaign => campaign.fixStaticReports)}
                  icon="fa-sharp fa-regular fa-sparkles"
                  loading={aiInsightsLoading}
                  onClick={handleAIButtonClick}
                  shape="link"
                  size="small"
                  text={formatMessage({
                    defaultMessage: 'AI Insights',
                    id: 'reporting.header.buttons.aiInsights'
                  })}
                  type="secondary"
                  visible={!noStats}
                />
              </CustomThemeProvider>
            )}
            {hasReportingDownloadV2 && (
              <ButtonUI
                icon="fa-regular fa-file-lines"
                id={`myReportsButton-${id}`}
                onClick={handleMyReportsButtonClick}
                shape="link"
                size="small"
                text={formatMessage({
                  defaultMessage: 'My Reports',
                  id: 'reporting.reportingDownload.myReportsButton'
                })}
                type="secondary"
              />
            )}
            <Tooltip
              config={{
                arrow: true,
                component: (
                  <div className="reportingHeader__downloadReport">
                    {Object.values(buttons)
                      .map(renderItem)
                      .map((b, i) => (
                        <Fragment key={i}>{b}</Fragment>
                      ))}
                    <LastDownloads id={`lastDownloads-${id}`} />
                  </div>
                ),
                forcePlacement: false,
                interactions: [TooltipInteractionEnum.click],
                padding: '0',
                placement: 'bottom',
                portal: false,
                strategy: 'fixed'
              }}
            >
              <ButtonUI
                badge={shouldShowBadge ? { badgeColor: 'var(--color-status-success-500)' } : false}
                disabled={
                  !Array.isArray(campaigns) || campaigns.length === 0 || stats === null || progressEvents === undefined
                }
                shape="squareRounded"
                size="small"
                text={formatMessage({
                  defaultMessage: 'Download Report',
                  id: 'reporting.header.buttons.downloadReport'
                })}
                type="primary"
              />
            </Tooltip>
            {!isSharedReport ? (
              <Tooltip
                config={{
                  arrow: true,
                  component: renderMenu(),
                  forcePlacement: true,
                  interactions: [TooltipInteractionEnum.click],
                  padding: '0',
                  placement: 'bottom-end',
                  portal: false,
                  strategy: 'fixed'
                }}
              >
                <ButtonUI
                  className="reportingHeader__menuButton"
                  icon="fa-solid fa-ellipsis-vertical"
                  shape="squareRounded"
                  size="small"
                  type="secondary"
                />
              </Tooltip>
            ) : null}
          </SPCHeader.Actions>
        </SPCHeader>
        {campaigns.length > 0 && <Shortcuts />}
        {myReportModalIsOpen && (
          <MyReportsModal id="myReportsModal" isOpen={myReportModalIsOpen} onClose={handleMyReportsModalClose} />
        )}
        {downloadReportIsOpen && (
          <ReportDownloadSection
            campaigns={campaigns}
            cartography={cartography}
            onBackClick={handleDownloadReportBackToggled}
            showGTotal={showGTotal}
            stats={stats}
            user={user}
            userRoles={userRoles}
            // biome-ignore lint/suspicious/noPrototypeBuiltins: off
            {...(initialData?.hasOwnProperty('entityId') && { initialData })}
          />
        )}
        {coverageStudyIsOpen && (
          <CoverageStudySection
            campaigns={campaigns}
            onBackClick={() => handleCoverageStudyToggled(false)}
            user={user}
            userRoles={userRoles}
          />
        )}
        {!noStats && (
          <AIInsightsModal
            campaigns={campaigns}
            cartography={cartography}
            id="aiInsightsModal"
            isOpen={aiInsightsModalIsOpen}
            onClose={handleAIInsightsClose}
            onInsightsDataReady={handleAIInsightsDataReady}
            onInsightsLoading={handleAIInsightsLoading}
            showGTotal={showGTotal}
            stats={stats}
            user={user}
            userRoles={userRoles}
          />
        )}
      </HeaderStyle>
      {isPDFLoading && (
        <div className="reportingDownloadPDFSpinner">
          <Loader showBackground type="landing" variant="default" />
        </div>
      )}
    </>
  );
}

export default Header;
